/* eslint-disable react-hooks/exhaustive-deps */
import React, {
    createContext,
    FunctionComponent,
    MouseEvent,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import cn from 'classnames';
import noop from 'lodash/noop';
import once from 'lodash/once';
import { connect, ConnectedProps, useSelector } from 'react-redux';

import { RootState } from '../../../../../init-store';
import { ButtonProps } from '../../../../shared/components/button';
import { SELF } from '../../../../shared/constants';
import { isMessengerIntegration } from '../../../../shared/features/config/selectors';
import { NotificationType, notify } from '../../../../shared/features/notifications/ps-notifications';
import { parentFrameCommunicator } from '../../../../shared/features/parent-frame-communicator';
import { SettingsSectionKey } from '../../../../shared/features/settings-modal/constants';
import { openSettingsModalAction } from '../../../../shared/features/settings-modal/slice';
import { i18n } from '../../../../shared/helpers/i18n';
import { ACTION, FEATURE } from '../../../../shared/helpers/logger-enums';
import { createLogger } from '../../../../shared/helpers/loggerFacade';
import { countMetrika } from '../../../../shared/helpers/metrika';
import { useActions } from '../../../../shared/hooks';
import { useElementWidth } from '../../../../shared/hooks/useElementWidth';
import { BroadcastStatus } from '../../../../types';
import { ConferenceStatus, GRID_VIEW, MediaStatus, NetworkState, ShareDesktopStatus } from '../../constants';
import { screenWidths } from '../../helpers/constants';
import { OutgoingEvents } from '../../iframe-sagas/types';
import {
    canControlBroadcast,
    getBroadcastStatus,
    getChatId,
    getChatWidgetVisible,
    getConferenceId,
    getConferenceStatus,
    getIsWaitingForCallee,
    getNetworkState,
    getParticipant,
    getRoomId,
    getSidebarContent,
    isChatAllowedInConference,
    isLocalRecordingActive,
    isLocalRecordingAllowed,
    isSelfModerator,
    selectIsBroadcastEnabled,
    selectIsPermissionsExtendedEnabled,
    selectIsReactionsAvailable,
    isSidebarVisible as selectIsSidebarVisible,
} from '../../selectors';
import {
    CancelCallActionParams,
    DisconnectReason,
    NotificationId,
    ParticipantState,
    SidebarContent,
    ToggleMuteActionInterface,
} from '../../types';
import { createBroadcastAction } from '../broadcast/slice';
import {
    createChatAction,
    getChatUnreadCount,
    getIsChatLoading,
    getIsChatUserJoined,
    getIsChatWidgetReady,
    setIsChatLoading,
    toggleChatVisibilityAction,
} from '../chat/slice';
import { selectCloudRecordingStatus } from '../cloud-recording';
import {
    isPermissionsChangeAllowedSelector,
    isRoleModelEnabledSelector,
    selectHasCameraPermission,
    selectHasCloseRoomPermission,
    selectHasDesktopPermission,
    selectHasMicrophonePermission,
    selectHasReactionsPermission,
} from '../permissions';
import { setSidebarContent } from '../sidebar/slice';
import { selectIsSummarizationAvailable, selectSummarizationStatus } from '../summarization';
import { CollapseBreakpoint } from './constants';
import { useToolbarButtonClickHandlerMaker } from './hooks';
import { useCloudRecordingControl } from './hooks/use-cloud-recording-control';
import { useLocalRecordingControl } from './hooks/use-local-recording-control';
import { ShareDesktopOption } from './share-desktop-option';
import classes from './style.css';
import BroadcastButton from './toolbar-buttons/broadcast-button';
import { CameraButton } from './toolbar-buttons/camera-button';
import { ToolbarChatButton } from './toolbar-buttons/chat-button';
import { CopyLinkButton } from './toolbar-buttons/copy-link-button';
import { EndCallButton } from './toolbar-buttons/end-call-button';
import { MicrophoneButton } from './toolbar-buttons/microphone-button';
import { MoreButton, MoreButtonWithNotifications } from './toolbar-buttons/more-button';
import { ParticipantsButton, ParticipantsCount } from './toolbar-buttons/participants-button';
import { ReactionButton, ReactionOption } from './toolbar-buttons/reactions-control';
import ShareDesktopButton from './toolbar-buttons/share-desktop-button';
import { ToolbarButton } from './toolbar-buttons/toolbar-button';
import { SummarizationButton, SummarizationOption } from './toolbar-controls/summarization';
import { ToolbarOption } from './toolbar-option';

// Сюда входят два отступа по бокам от тулбара (по 8px) и 5 кнопок c отступами (56px + 8px)
// Кнопки: копирование ссылки на конференцию, микрофон, камера, moreButton и завершение конференции
const RESERVED_TOOLBAR_WIDTH = 16 + 64 * 5;

const mapState = (state: RootState) => {
    const self = getParticipant(state, SELF);
    return {
        conferenceId: getConferenceId(state),
        isLocalRecordingAllowed: isLocalRecordingAllowed(state),
        isRecording: isLocalRecordingActive(state),
        audioMuted: Boolean(!self.audioTrackState || self.audioTrackState?.muted),
        videoMuted: Boolean(!self.videoTrackState || self.videoTrackState?.muted),
        canJoin: getConferenceStatus(state) === ConferenceStatus.READY_TO_JOIN,
        isChatAllowed: isChatAllowedInConference(state),
        chatWidgetVisible: getChatWidgetVisible(state),
        sidebarContent: getSidebarContent(state),
        chatUnreadCount: getChatUnreadCount(state),
        chatId: getChatId(state),
        isChatWidgetReady: getIsChatWidgetReady(state),
        isChatLoading: getIsChatLoading(state),
        isReconnect: getNetworkState(state) !== NetworkState.ONLINE,
        isChatUserJoined: getIsChatUserJoined(state),
        isBroadcastEnabled: selectIsBroadcastEnabled(state),
        canControlBroadcast: canControlBroadcast(state),
        broadcastStatus: getBroadcastStatus(state),
        isMessengerIntegration: isMessengerIntegration(state),
        isWaitingForCallee: getIsWaitingForCallee(state),
    };
};

const mapDispatchToProps = {
    createBroadcast: createBroadcastAction,
    setSidebarContent,
    createChat: createChatAction,
    setIsChatLoading,
    showNotification: notify,
};

const connector = connect(mapState, mapDispatchToProps);

interface ToolbarContextType {
    buttonSize: ButtonProps['size'];
}
export const ToolbarContext = createContext<ToolbarContextType>({ buttonSize: 'lg' });

type ToolbarProps = ConnectedProps<typeof connector> & {
    toggleMute(params: ToggleMuteActionInterface): void;
    shareScreen(): void;
    unshareScreen(): void;
    centerContent?: ReactNode;
    onClickCancel?(): void;
    className?: string;
    isDesktopSharingEnabled?: boolean;
    desktopSharingStatus: string;
    state?: ParticipantState;
    participantsCount?: number;
    joinedAsGuest: boolean;
    isJoining: boolean;
    gridView: GRID_VIEW;
    cancel(params?: CancelCallActionParams): void;
    onClickChangeGridView(view: GRID_VIEW): void;
    isYandexTeamUser?: boolean;
    isChatAllowed: boolean;
    hideDesktopSharingButton?: boolean;
    hideBroadcastButton?: boolean;
    isOverlay?: boolean;
};

export interface ConferenceGuestConfig {
    audioMuted?: boolean;
    videoMuted?: boolean;
    displayName?: string;
}

const logger = createLogger('toolbar');

const Toolbar: FunctionComponent<ToolbarProps> = (props) => {
    const {
        className,
        centerContent,
        onClickCancel,
        desktopSharingStatus,
        isDesktopSharingEnabled,
        state,
        joinedAsGuest,
        isJoining,
        isRecording,
        isLocalRecordingAllowed,
        isChatAllowed,
        chatWidgetVisible,
        sidebarContent,
        chatUnreadCount,
        setSidebarContent,
        chatId,
        createChat,
        isReconnect,
        isChatUserJoined,
        showNotification,
        isBroadcastEnabled,
        canControlBroadcast,
        createBroadcast,
        broadcastStatus,
        isMessengerIntegration,
        isWaitingForCallee,
        hideDesktopSharingButton,
        participantsCount,
        isOverlay,
    } = props;

    const roomId = useSelector(getRoomId);
    const conferenceStatus = useSelector(getConferenceStatus);
    const isJoined = conferenceStatus === ConferenceStatus.JOINED;

    const isModerator = useSelector(isSelfModerator);
    const isRoleModelEnabled = useSelector(isRoleModelEnabledSelector);
    const isReactionsAvailable = useSelector(selectIsReactionsAvailable);
    const hasReactionsPermission = useSelector(selectHasReactionsPermission);

    // Расширенные права участников
    const isPermissionsExtendedEnabled = useSelector(selectIsPermissionsExtendedEnabled);
    const hasCameraPermission = useSelector(selectHasCameraPermission);
    const hasMicrophonePermission = useSelector(selectHasMicrophonePermission);
    const hasDesktopPermission = useSelector(selectHasDesktopPermission);
    const hasCloseRoomPermission = useSelector(selectHasCloseRoomPermission);

    const isSummarizationAvailable = useSelector(selectIsSummarizationAvailable);
    const summarizationStatus = useSelector(selectSummarizationStatus);
    const isSidebarVisible = useSelector(selectIsSidebarVisible);
    const cloudRecordingStatus = useSelector(selectCloudRecordingStatus);
    const localRecordingActive = useSelector(isLocalRecordingActive);

    const actions = useActions({ openSettingsModalAction, toggleChatVisibilityAction });

    const isSummarizationButtonExpanded = summarizationStatus === 'RUNNING' || summarizationStatus === 'STARTING';
    const isCloudRecordingProcessing = cloudRecordingStatus === 'RUNNING' || cloudRecordingStatus === 'STARTING';
    const isBroadcastExists = broadcastStatus !== BroadcastStatus.NONE;

    const [isShowLimitationPopup, setLimitationPopup] = useState(false);

    const toggleLimitationPopup = useCallback(() => {
        setLimitationPopup(!isShowLimitationPopup);
    }, [isShowLimitationPopup]);

    const onShowBroadcastTutorialTooltip = useCallback(() => {
        setLimitationPopup(false);
    }, []);

    const collapseBreakpoint = (() => {
        const isHugeBreakpointCandidate =
            isSummarizationAvailable &&
            ((isSummarizationButtonExpanded && localRecordingActive) ||
                (isSummarizationButtonExpanded && isCloudRecordingProcessing) ||
                (localRecordingActive && isCloudRecordingProcessing));
        if (isHugeBreakpointCandidate && isBroadcastExists) {
            return CollapseBreakpoint.HUGE;
        }
        if (isHugeBreakpointCandidate || isBroadcastExists) {
            return isSidebarVisible ? CollapseBreakpoint.HUGE : CollapseBreakpoint.DEFAULT;
        }
        if (isSummarizationButtonExpanded) {
            return CollapseBreakpoint.DEFAULT;
        }
        return CollapseBreakpoint.SMALL;
    })();

    const [toolbarRef, toolbarWidth] = useElementWidth();

    const minButtonWidth = 64;

    const reactionButtonWidth = isReactionsAvailable ? minButtonWidth : 0;
    const smallScreenWidth = screenWidths.small + reactionButtonWidth;
    const mediumScreenWidth = screenWidths.medium + reactionButtonWidth;

    const hasTinyScreen = toolbarWidth <= screenWidths.tiny;
    const hasSmallScreen = toolbarWidth < smallScreenWidth;
    const hasMediumScreen = toolbarWidth >= smallScreenWidth && toolbarWidth <= mediumScreenWidth;
    const hasMiniMediumScreen = toolbarWidth >= smallScreenWidth && toolbarWidth <= mediumScreenWidth - minButtonWidth;
    const hasBigMediumScreen = toolbarWidth >= mediumScreenWidth && toolbarWidth <= screenWidths.bigMedium;

    const makeAction = useToolbarButtonClickHandlerMaker();

    const onceSendCancelMeetingMetrics = useMemo(
        () =>
            once((roomId) => {
                countMetrika('meeting_screen', 'drop', roomId as string);
            }),
        []
    );

    const onBeforeUnload = useCallback(() => {
        if (!props.conferenceId) {
            return;
        }

        onceSendCancelMeetingMetrics(roomId);
        // нам не надо перезапускаться если изменился conferenceId
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const onToggleShowChatWidget = useCallback(() => {
        if (!chatWidgetVisible) {
            logger.startFeatureDelta({
                feature: FEATURE.CHAT,
                action: ACTION.OPEN,
            });
        }
        actions.toggleChatVisibilityAction(!chatWidgetVisible);
    }, [chatWidgetVisible]);

    const onClickChatButton = useCallback(() => {
        if (isMessengerIntegration) {
            parentFrameCommunicator.sendMessage('messenger', { event: OutgoingEvents.CHAT_OPEN });

            return;
        }

        if (!chatId) {
            logger.startFeatureDelta({
                feature: FEATURE.CHAT,
                action: ACTION.CREATE,
            });

            createChat();
            return;
        }

        if (!isReconnect && isChatUserJoined) {
            onToggleShowChatWidget();
        }

        if (isReconnect || !isChatUserJoined) {
            // Если виджет в этот момент уже открыт, при клике на кнопку не надо показывать нотификацию, надо
            // дать его спокойно закрыть и ругаться если мы пытаемся снова его открыть.
            if (chatWidgetVisible) {
                onToggleShowChatWidget();
            } else {
                showNotification({
                    id: NotificationId.CHAT_OPEN_ERROR,
                    type: NotificationType.ERROR,
                    priority: 1,
                    text: i18n('statuses', 'chat-open-error', {
                        spacebreak: '\n',
                    }),
                    timeout: 3000,
                    icon: 'error',
                });
            }
        }
        // нам не надо отслеживать createChat и showNotification
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatId, chatWidgetVisible, isChatUserJoined, isReconnect, onToggleShowChatWidget]);

    const onCreateBroadcastClick = useCallback(() => {
        createBroadcast({});
        countMetrika('meeting_screen', 'create_stream');
    }, [createBroadcast]);

    const displayNameInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        displayNameInputRef.current?.focus();

        window.addEventListener('beforeunload', onBeforeUnload, { once: true });
        window.addEventListener('popstate', onBeforeUnload, { once: true });

        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
            window.removeEventListener('popstate', onBeforeUnload);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const onClickSettings = useCallback(
        (event: MouseEvent, sectionKey?: SettingsSectionKey) => {
            actions.openSettingsModalAction({
                isHiddenSectionVisible: event.altKey,
                sectionKey,
            });
        },
        [actions]
    );

    const copyLinkButton = (
        <CopyLinkButton
            onClick={makeAction(() => null, 'invite')}
            visible={!isOverlay}
            title={i18n('common', 'share-button-text')}
            key="copyLinkButton"
            testId="share-button"
        />
    );

    const isAudioMuted = !state?.audio || state?.audio?.muted;
    const isVideoMuted = !state?.video || state?.video?.muted;

    const videoStatus = (() => {
        if (isPermissionsExtendedEnabled && !hasMicrophonePermission) {
            return MediaStatus.BLOCKED;
        }
        if (isAudioMuted) {
            return MediaStatus.MUTED;
        }
        return MediaStatus.UNMUTED;
    })();
    const toggleAudioButton = (
        <MicrophoneButton
            status={videoStatus}
            onToggleMute={makeAction(
                () => {
                    logger.startFeatureDelta({
                        feature: FEATURE.MUTE,
                        action: isAudioMuted ? ACTION.MIC_ON : ACTION.MIC_OFF,
                    });
                    props.toggleMute({ type: 'audio' });
                },
                `mic_${isAudioMuted ? 'on' : 'off'}`
            )}
            title={isAudioMuted ? i18n('toolbar', 'turn-on-mic') : i18n('toolbar', 'turn-off-mic')}
            testId={isAudioMuted ? 'turn-on-mic-button' : 'turn-off-mic-button'}
            key="toggleAudioButton"
        />
    );

    const cameraStatus = (() => {
        if (isPermissionsExtendedEnabled && !hasCameraPermission) {
            return MediaStatus.BLOCKED;
        }
        if (isVideoMuted) {
            return MediaStatus.MUTED;
        }
        return MediaStatus.UNMUTED;
    })();
    const toggleVideoButton = (
        <CameraButton
            status={cameraStatus}
            onToggleMute={makeAction(
                () => {
                    logger.startFeatureDelta({
                        feature: FEATURE.MUTE,
                        action: isVideoMuted ? ACTION.CAMERA_ON : ACTION.CAMERA_OFF,
                    });
                    props.toggleMute({ type: 'video' });
                },
                `camera_${isVideoMuted ? 'on' : 'off'}`
            )}
            title={isVideoMuted ? i18n('toolbar', 'turn-on-camera') : i18n('toolbar', 'turn-off-camera')}
            testId={isVideoMuted ? 'turn-on-camera-button' : 'turn-off-camera-button'}
            key="toggleVideoButton"
        />
    );

    const audioAndVideoToggler = (
        <>
            {toggleAudioButton}
            {toggleVideoButton}
        </>
    );

    const [summarizationButtonRef, summarizationButtonWidth] = useElementWidth();

    const showSummarization = isSummarizationAvailable && isJoined;

    const summarizationCollapseBreakpoint = (() => {
        if (isSidebarVisible) {
            return CollapseBreakpoint.SMALL;
        }
        if (isBroadcastExists) {
            return CollapseBreakpoint.TINY;
        }
        return CollapseBreakpoint.SINGULAR;
    })();
    const summarizationButton =
        showSummarization && (!isBroadcastExists || !hasTinyScreen) ? (
            <div ref={summarizationButtonRef}>
                <SummarizationButton key="summarization-button" collapseBreakpoit={summarizationCollapseBreakpoint} />
            </div>
        ) : null;
    const summarizationOption =
        showSummarization && !summarizationButton ? <SummarizationOption key="summarization-option" /> : null;

    const [handButtonRef, handButtonWidth] = useElementWidth();

    const showHand = isReactionsAvailable && hasReactionsPermission && isJoined;

    const handButton =
        showHand && !hasSmallScreen ? (
            <div ref={handButtonRef}>
                <ReactionButton reactionType="hand" key="reaction-hand" />
            </div>
        ) : null;
    const handOption = showHand && hasSmallScreen ? <ReactionOption reactionType="hand" key="reaction-hand" /> : null;

    const [shareDesktopButtonRef, shareDesktopButtonWidth] = useElementWidth();
    const isShareDesktopButtonDisabled = !isDesktopSharingEnabled || desktopSharingStatus === 'remote' || !isJoined;
    const isSharingDesktop = desktopSharingStatus === 'local';
    const isUnshare = Boolean(state?.desktop && !state.desktop.muted);

    const shareDesktopStatus = (() => {
        if (isPermissionsExtendedEnabled && !hasDesktopPermission) {
            return ShareDesktopStatus.BLOCKED;
        }
        return isSharingDesktop ? ShareDesktopStatus.SHARING : ShareDesktopStatus.UNSHARING;
    })();
    const shareDesktopOption = (
        <ShareDesktopOption
            status={shareDesktopStatus}
            onToggleShareScreen={makeAction(
                () => (isUnshare ? props.unshareScreen() : props.shareScreen()),
                `share_screen_${isUnshare ? 'stopped' : 'start'}`
            )}
            dotColor={desktopSharingStatus === 'remote' ? 'green' : undefined}
            disabled={isShareDesktopButtonDisabled}
        />
    );

    const [chatButtonRef, chatButtonWidth] = useElementWidth();
    const chatButton = isChatAllowed && (
        <div ref={chatButtonRef}>
            <ToolbarChatButton
                chatMessagesCount={chatWidgetVisible ? 0 : chatUnreadCount}
                visible={isChatAllowed}
                disabled={isWaitingForCallee}
                onClick={onClickChatButton}
                key="chatButton"
                collapseBreakpoint={collapseBreakpoint}
            />
        </div>
    );

    const chatOption = (
        <ToolbarOption
            onClick={onClickChatButton}
            content={
                <div className={classes.chatOption_container}>
                    {!!chatUnreadCount && (
                        <div className={classes.chatOption_badge}>
                            <div className={classes.chatOption_badgeText}>
                                {chatUnreadCount > 99 ? '99+' : chatUnreadCount}
                            </div>
                        </div>
                    )}
                    {i18n('toolbar', 'chat-button-title')}
                </div>
            }
            title={i18n('toolbar', 'chat-button-alt-title')}
            iconName="chat"
            key="chatOption"
        />
    );

    const settingsOnClick = makeAction((event) => onClickSettings(event as MouseEvent), 'settings');

    const settingsOption = (
        <ToolbarOption
            iconName="settings"
            title={i18n('toolbar', 'open-settings')}
            content={i18n('toolbar', 'more-popup-option-settings')}
            onClick={settingsOnClick}
            key="settingsOption"
        />
    );

    const settingsButton = (
        <ToolbarButton
            className={classes.gridViewChangeButton}
            title={i18n('toolbar', 'open-settings')}
            iconName="settings"
            onClick={settingsOnClick}
            key="settingsButton"
            testId="open-settings-button"
        />
    );

    const changeGridProps = {
        onClick: makeAction(
            () =>
                props.onClickChangeGridView(
                    props.gridView === GRID_VIEW.DEFAULT ? GRID_VIEW.PRESENTER : GRID_VIEW.DEFAULT
                ),
            props.gridView === 'default' ? 'to_presenter' : 'to_grid'
        ),
        iconName: props.gridView === 'default' ? 'presenterView' : ('defaultView' as 'presenterView' | 'defaultView'),
        disabled: !isJoined || desktopSharingStatus !== 'none',
        title: props.gridView === 'default' ? i18n('toolbar', 'presenter-view') : i18n('toolbar', 'gallery-view'),
        content: props.gridView === 'default' ? i18n('toolbar', 'presenter-view') : i18n('toolbar', 'gallery-view'),
    };

    const gridViewChangeOption = <ToolbarOption {...changeGridProps} key="gridViewChangeOption" />;

    const gridViewChangeButton = (
        <ToolbarButton className={classes.gridViewChangeButton} key="gridViewChangeButton" {...changeGridProps} />
    );

    const broadcastOption = (
        <ToolbarOption
            onClick={onCreateBroadcastClick}
            iconName="broadcastEnabled"
            content={i18n('toolbar', 'create-broadcast')}
            title={i18n('toolbar', 'create-broadcast')}
            key="broadcastOption"
        />
    );

    const [broadcastButtonRef, broadcastButtonWidth] = useElementWidth();
    const broadcastButton =
        broadcastStatus === BroadcastStatus.NONE || !isBroadcastEnabled || !canControlBroadcast ? null : (
            <div ref={broadcastButtonRef}>
                <BroadcastButton
                    isShowLimitationPopup={isShowLimitationPopup}
                    onShowBroadcastTutorialTooltip={onShowBroadcastTutorialTooltip}
                    key="broadcastButton"
                    isCompact={
                        hasSmallScreen ||
                        (isSummarizationAvailable && (hasMediumScreen || hasBigMediumScreen || hasMiniMediumScreen))
                    }
                />
            </div>
        );

    const onParticipantsClick = useCallback(() => {
        if (sidebarContent !== SidebarContent.Participants) {
            countMetrika('meeting_screen', 'participants_list', 'open');
            setSidebarContent(SidebarContent.Participants);
        } else {
            setSidebarContent(null);
        }
    }, [setSidebarContent, sidebarContent]);

    const [participantsButtonRef, participantsButtonWidth] = useElementWidth();
    const participantsButtonVisible =
        isJoined &&
        ((!isSummarizationButtonExpanded && !hasSmallScreen) ||
            (isSummarizationButtonExpanded && !hasSmallScreen && !hasMiniMediumScreen));
    const participantsButton = (
        <div ref={participantsButtonRef}>
            <ParticipantsButton
                key="participantsButton"
                disabled={isWaitingForCallee}
                onClick={onParticipantsClick}
                collapseBreakpoint={collapseBreakpoint}
            />
        </div>
    );
    const participantsOption = (
        <ToolbarOption
            onClick={onParticipantsClick}
            iconName="participants"
            content={
                <div className={classes.participantsOption}>
                    <div className={classes.participantsOptionText}>{i18n('toolbar', 'participants-button-title')}</div>
                    <ParticipantsCount count={participantsCount} withDot />
                </div>
            }
            title={i18n('toolbar', 'participants-button-title')}
            key="participantsOption"
        />
    );

    const isPermissionsChangeAllowed = useSelector(isPermissionsChangeAllowedSelector);
    const permissionsOption = (
        <ToolbarOption
            onClick={(event) => onClickSettings(event, SettingsSectionKey.Permissions)}
            iconName="permissions"
            content={i18n('settings', 'permissions-members')}
            title={i18n('settings', 'permissions-members')}
            key="permissionsOption"
        />
    );

    const maxCenterWidth = toolbarWidth - RESERVED_TOOLBAR_WIDTH;

    const renderedButtons = [
        summarizationButtonWidth,
        handButtonWidth,
        shareDesktopButtonWidth,
        participantsButtonWidth,
        chatButtonWidth,
        broadcastButtonWidth,
    ];

    const renderedButtonsWidth = useMemo(() => {
        return renderedButtons.reduce((acc, width) => acc + (width ? width + 8 : 0), 0);
    }, [toolbarWidth, ...renderedButtons]);

    const { cloudRecordingButton, cloudRecordingOption, cloudRecordingButtonWidth } = useCloudRecordingControl(
        maxCenterWidth - renderedButtonsWidth,
        {
            canButtonAllocate:
                !isSummarizationAvailable ||
                (isSummarizationAvailable &&
                    !hasSmallScreen &&
                    !hasMiniMediumScreen &&
                    !hasMediumScreen &&
                    !hasBigMediumScreen),
        }
    );
    const { localRecordingOption, localRecordingButton } = useLocalRecordingControl(
        maxCenterWidth - renderedButtonsWidth - cloudRecordingButtonWidth,
        {
            limitationPopupOptions: {
                isShowLimitationPopup: isRecording && isShowLimitationPopup,
                toggleLimitationPopup: toggleLimitationPopup,
            },
            canButtonAllocate:
                !isSummarizationAvailable ||
                (isSummarizationAvailable &&
                    !hasSmallScreen &&
                    !hasMiniMediumScreen &&
                    !hasMediumScreen &&
                    !hasBigMediumScreen),
        }
    );

    const shareDesktopButton = (
        <div ref={shareDesktopButtonRef}>
            <ShareDesktopButton
                visible={isJoined}
                disabled={isWaitingForCallee || isShareDesktopButtonDisabled}
                status={shareDesktopStatus}
                onToggleShareScreen={makeAction(
                    () => (isUnshare ? props.unshareScreen() : props.shareScreen()),
                    `share_screen_${isUnshare ? 'stopped' : 'start'}`
                )}
                collapseBreakpoint={collapseBreakpoint}
                key="shareDesktopButton"
            />
        </div>
    );

    const isChatInMoreMenu =
        hasSmallScreen ||
        hasMiniMediumScreen ||
        (isSummarizationButtonExpanded && (hasMediumScreen || hasBigMediumScreen)) ||
        (hasMediumScreen && Boolean(broadcastButton));

    const memoizedItems = useMemo(() => {
        const items = [];
        const renderBroadcastOptions =
            canControlBroadcast && isBroadcastEnabled && broadcastStatus === BroadcastStatus.NONE;

        if (cloudRecordingOption) {
            items.push(cloudRecordingOption);
        }

        if (localRecordingOption) {
            items.push(localRecordingOption);
        }

        if (cloudRecordingOption || localRecordingOption) {
            items.push(<div key="separator" className={classes.separator} />);
        }

        if (summarizationOption) {
            items.push(summarizationOption);
        }

        if (handOption) {
            items.push(handOption);
        }

        if (isJoined) {
            items.push(gridViewChangeOption);
        }

        if (
            (hasSmallScreen || (hasMediumScreen && Boolean(broadcastButton))) &&
            isJoined &&
            !hideDesktopSharingButton
        ) {
            items.push(shareDesktopOption);
        }

        if (hasSmallScreen && isJoined) {
            items.push(participantsOption);
        }

        if (isChatAllowed && isChatInMoreMenu && isJoined) {
            items.push(chatOption);
        }

        if (renderBroadcastOptions) {
            items.push(broadcastOption);
        }

        if (isJoined && isPermissionsChangeAllowed) {
            items.push(permissionsOption);
        }

        if (isJoined) {
            items.push(settingsOption);
        }

        return items;
    }, [
        canControlBroadcast,
        isBroadcastEnabled,
        broadcastStatus,
        isJoined,
        hasSmallScreen,
        hasMediumScreen,
        broadcastButton,
        hideDesktopSharingButton,
        hasMiniMediumScreen,
        isChatAllowed,
        isLocalRecordingAllowed,
        isPermissionsChangeAllowed,
        gridViewChangeOption,
        shareDesktopOption,
        chatOption,
        localRecordingOption,
        cloudRecordingOption,
        broadcastOption,
        participantsOption,
        settingsOption,
        permissionsOption,
        hasBigMediumScreen,
        handOption,
        summarizationOption,
    ]);

    const MoreButtonComponent = participantsButtonVisible ? MoreButton : MoreButtonWithNotifications;
    const moreButton = (
        <MoreButtonComponent
            onClick={makeAction(() => null, 'morePopup')}
            items={memoizedItems}
            popupClassName={chatWidgetVisible ? classes.moreButtonPopoverWithOpenChat : ''}
            visible={isJoined || hasSmallScreen}
            isShowLimitationPopup={isShowLimitationPopup}
            toggleLimitationPopup={toggleLimitationPopup}
            disabled={isWaitingForCallee}
            key="moreButton"
            placement={hasSmallScreen || hasMediumScreen ? 'top right' : 'top left'}
            showDots={!participantsButtonVisible}
            hasOtherSharing={desktopSharingStatus === 'remote'}
        />
    );

    const isDesktopJoining = !isJoined && joinedAsGuest;

    const shouldShowEndCallOptions =
        isRoleModelEnabled &&
        (isPermissionsExtendedEnabled ? hasCloseRoomPermission : isModerator) &&
        (isJoined || isDesktopJoining) &&
        participantsCount !== undefined &&
        participantsCount > 1;
    const endCallButton = (
        <EndCallButton
            onClick={onClickCancel ? onClickCancel : () => props.cancel({ source: DisconnectReason.END_CALL_BUTTON })}
            clickMetrika={onClickCancel ? noop : makeAction(noop, 'drop', roomId)}
            withAccent={(!isDesktopJoining || isMessengerIntegration) && !isSharingDesktop && !isRecording}
            shouldShowOptions={shouldShowEndCallOptions}
            key="endCallButton"
        />
    );

    const rightButtons = useMemo(() => {
        if (isJoined || (isJoining && !isMessengerIntegration && !isOverlay)) {
            const buttons = [endCallButton];

            if ((hasSmallScreen || hasMediumScreen) && isJoined) {
                buttons.unshift(moreButton);
            }

            return buttons;
        }

        if (isMessengerIntegration) {
            const buttons = [endCallButton];

            if (!hasSmallScreen) {
                buttons.unshift(settingsButton);
            }

            return buttons;
        }

        return settingsButton;
    }, [
        isJoined,
        isMessengerIntegration,
        endCallButton,
        settingsButton,
        hasSmallScreen,
        hasMediumScreen,
        isJoining,
        moreButton,
        isOverlay,
    ]);

    const leftButtons = useMemo(() => {
        return [...(isMessengerIntegration ? [] : [copyLinkButton]), audioAndVideoToggler];
    }, [copyLinkButton, audioAndVideoToggler, isMessengerIntegration]);

    return (
        <ToolbarContext.Provider value={{ buttonSize: 'lg' }}>
            <div ref={toolbarRef} className={cn(classes.wrapper, className)}>
                <div className={cn(classes.container, { [classes.overlay]: isOverlay, [classes.joined]: isJoined })}>
                    <div className={classes.left}>{leftButtons}</div>
                    <div className={classes.center}>
                        {centerContent || (
                            <>
                                {summarizationButton}
                                {handButton}
                                {hideDesktopSharingButton ||
                                hasSmallScreen ||
                                (hasMediumScreen && Boolean(broadcastButton))
                                    ? null
                                    : shareDesktopButton}
                                {participantsButtonVisible ? participantsButton : null}
                                {isChatAllowed && isChatInMoreMenu ? null : chatButton}
                                {broadcastButton}
                                {cloudRecordingButton}
                                {isJoining ? null : isLocalRecordingAllowed ? (
                                    <>
                                        {localRecordingButton}
                                        {hasSmallScreen || hasMediumScreen ? null : moreButton}
                                    </>
                                ) : isChatAllowed ? (
                                    hasSmallScreen || hasMediumScreen ? null : (
                                        moreButton
                                    )
                                ) : (
                                    <>
                                        {hasMiniMediumScreen || hasSmallScreen || hasMediumScreen
                                            ? null
                                            : gridViewChangeButton}
                                        {hasMediumScreen || hasSmallScreen ? null : settingsButton}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                    <div className={classes.right}>{rightButtons}</div>
                </div>
            </div>
        </ToolbarContext.Provider>
    );
};

export default connector(Toolbar);
